import { call, put, select } from 'typed-redux-saga';
import { ActionType } from 'typesafe-actions';
import { APIResult, callAPI } from '../../callApi';
import { createAsyncActionResource } from '../util';
import {
  ApiActionType,
  EndPointBuilder,
  KeyComparer,
  KeyOnlyPayload,
  MainResourceHandler,
  MainResourceState,
  MainStateSelector,
  QueryParamSelector,
} from './types';
import { createErrorHandler } from './util';

// This action deletes a record by sending a DELETE request to the server.
//    Should only be used for non-nested resources.
export function createDeleteAction<
  TItemKey,
  TItemSummary extends TItemKey,
  TItemBody extends TItemKey,
>(
  displayName: string,
  getEndpoint: EndPointBuilder<TItemKey, TItemSummary, TItemBody>,
  selector: MainStateSelector<TItemKey, TItemSummary, TItemBody>,
  sameKey: KeyComparer<TItemKey, TItemSummary, TItemBody>,
  queryParamSelector: QueryParamSelector<TItemBody>,
) {
  const deleteAsync = createAsyncActionResource<
    KeyOnlyPayload<TItemKey>,
    KeyOnlyPayload<TItemKey>
  >(displayName, 'delete');

  function doDelete(
    key: TItemKey,
    lockNum: number | undefined,
    queryParams: any,
  ): Promise<APIResult> {
    return callAPI(getEndpoint(key), 'DELETE', { lockNum, queryParams });
  }

  function* deleteRequestSaga(
    params: ActionType<typeof deleteAsync.request>,
  ): Generator {
    try {
      const { key, onSuccess } = params.payload;

      const state = (yield select(selector)) as MainResourceState<
        TItemKey,
        TItemSummary,
        TItemBody
      >;

      const record = state.records.find(r => sameKey(r.summary, key));
      if (!record) {
        console.error('Could not find record to delete', key);
        return;
      }

      yield* call(
        doDelete,
        key,
        record.loadedLockNum,
        queryParamSelector(record.body),
      );
      yield put(deleteAsync.success({ key, onSuccess }));
    } catch (error) {
      yield put(deleteAsync.failure(error));
    }
  }

  const deleteRequestHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof deleteAsync.request
  > = state => {
    return {
      ...state,
      pendingUpdates: state.pendingUpdates + 1,
      error: undefined,
      patchData: undefined,
    };
  };

  const deleteSuccessHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof deleteAsync.success
  > = (state, action) => {
    const { key } = action.payload;
    const records = state.records.filter(
      record => !sameKey(record.summary, key),
    );

    return {
      ...state,
      pendingUpdates: state.pendingUpdates - 1,
      records,
      currentRecordIndex: undefined,
      currentRecord: undefined,
      currentBody: undefined,
      currentItems: records.map(r => r.summary),
      lastLoadParams: undefined,
      lastRecordsChangedAction: ApiActionType.delete,
    };
  };

  function* deleteSuccessSaga(action: ActionType<typeof deleteAsync.success>) {
    const { onSuccess } = action.payload;
    onSuccess && onSuccess();
  }

  function* deleteFailureSaga() {
    // TODO
  }

  const deleteFailureHandler = createErrorHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof deleteAsync.failure
  >(true);

  return {
    deleteAsync,
    deleteRequestSaga,
    deleteRequestHandler,
    deleteSuccessSaga,
    deleteSuccessHandler,
    deleteFailureSaga,
    deleteFailureHandler,
  };
}
